/**
 * useValidation Hook
 * 
 * Centralized validation state management for invoice forms.
 * Provides field-level validation with user-friendly error messages.
 * 
 * Features:
 * - On-blur validation (non-intrusive)
 * - Auto-clear errors when values become valid
 * - Centralized error state management
 * - Support for all invoice field types
 * - FEATURE #7: Backend validation wiring (backend is single source of truth)
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  validateEmailWithMessage,
  validatePhoneWithMessage,
  validateRequiredWithMessage,
  validateQuantityWithMessage,
  validateRateWithMessage,
  validateDiscountWithMessage,
  validateTaxRateWithMessage,
} from '../utils/validators';
import { toNumber } from '../utils/calculations';
import { validateInvoiceDraft } from '../services/api';

/**
 * Field validation configuration
 * Maps field names to their validator functions
 * 
 * FEATURE #7: invoice_number removed - backend generates it on finalization
 */
const FIELD_VALIDATORS = {
  // Email fields
  business_email: (value) => validateEmailWithMessage(value, false),
  client_email: (value) => validateEmailWithMessage(value, false),
  
  // Phone fields
  business_phone: (value) => validatePhoneWithMessage(value, false),
  client_phone: (value) => validatePhoneWithMessage(value, false),
  
  // Required text fields
  business_name: (value) => validateRequiredWithMessage(value, 'Business name'),
  client_name: (value) => validateRequiredWithMessage(value, 'Client name'),
  // invoice_number removed - FEATURE #7: backend generates invoice number on finalization
  
  // Numeric fields
  tax_rate: (value) => validateTaxRateWithMessage(value),
  discount: (value, formData) => validateDiscountWithMessage(value, toNumber(formData?.subtotal)),
};

/**
 * Line item field validators
 */
const LINE_ITEM_VALIDATORS = {
  quantity: (value) => validateQuantityWithMessage(value),
  rate: (value) => validateRateWithMessage(value),
  description: (value) => validateRequiredWithMessage(value, 'Description'),
};

/**
 * Custom hook for managing form validation state
 * 
 * @returns {Object} Validation state and handlers
 */
export function useValidation() {
  // Field errors state: { fieldName: errorMessage }
  const [errors, setErrors] = useState({});
  
  // Track which fields have been touched (blurred)
  const [touched, setTouched] = useState({});

  /**
   * Validate a single field and update error state
   * @param {string} fieldName - Name of the field to validate
   * @param {any} value - Current value of the field
   * @param {Object} formData - Full form data (for contextual validation)
   * @returns {boolean} Whether the field is valid
   */
  const validateField = useCallback((fieldName, value, formData = {}) => {
    const validator = FIELD_VALIDATORS[fieldName];
    
    if (!validator) {
      // No validator defined, field is valid
      return true;
    }
    
    const result = validator(value, formData);
    
    setErrors(prev => {
      if (result.isValid) {
        // Clear error if valid
        const { [fieldName]: _, ...rest } = prev;
        return rest;
      } else {
        // Set error message
        return { ...prev, [fieldName]: result.message };
      }
    });
    
    return result.isValid;
  }, []);

  /**
   * Validate a line item field
   * @param {number} index - Line item index
   * @param {string} field - Field name (quantity, rate, description)
   * @param {any} value - Field value
   * @returns {boolean} Whether the field is valid
   */
  const validateLineItem = useCallback((index, field, value) => {
    const validator = LINE_ITEM_VALIDATORS[field];
    
    if (!validator) {
      return true;
    }
    
    const result = validator(value);
    const errorKey = `items[${index}].${field}`;
    
    setErrors(prev => {
      if (result.isValid) {
        const { [errorKey]: _, ...rest } = prev;
        return rest;
      } else {
        return { ...prev, [errorKey]: result.message };
      }
    });
    
    return result.isValid;
  }, []);

  /**
   * Mark a field as touched (user has interacted with it)
   * @param {string} fieldName - Field name
   */
  const touchField = useCallback((fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  }, []);

  /**
   * Handle field blur - validate and mark as touched
   * @param {string} fieldName - Field name
   * @param {any} value - Current value
   * @param {Object} formData - Full form data
   */
  const handleBlur = useCallback((fieldName, value, formData = {}) => {
    touchField(fieldName);
    validateField(fieldName, value, formData);
  }, [touchField, validateField]);

  /**
   * Handle line item blur
   * @param {number} index - Line item index
   * @param {string} field - Field name
   * @param {any} value - Field value
   */
  const handleLineItemBlur = useCallback((index, field, value) => {
    const errorKey = `items[${index}].${field}`;
    touchField(errorKey);
    validateLineItem(index, field, value);
  }, [touchField, validateLineItem]);

  /**
   * Clear a specific field error
   * @param {string} fieldName - Field name
   */
  const clearError = useCallback((fieldName) => {
    setErrors(prev => {
      const { [fieldName]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Clear all errors
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  /**
   * Get error message for a field (only if touched)
   * @param {string} fieldName - Field name
   * @returns {string|null} Error message or null
   */
  const getError = useCallback((fieldName) => {
    if (!touched[fieldName]) return null;
    return errors[fieldName] || null;
  }, [errors, touched]);

  /**
   * Get error for a line item field
   * @param {number} index - Line item index
   * @param {string} field - Field name
   * @returns {string|null} Error message or null
   */
  const getLineItemError = useCallback((index, field) => {
    const errorKey = `items[${index}].${field}`;
    if (!touched[errorKey]) return null;
    return errors[errorKey] || null;
  }, [errors, touched]);

  /**
   * Check if form has any validation errors
   */
  // hasErrors is superseded by hasAnyErrors below — kept for reference only
  // eslint-disable-next-line no-unused-vars
  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  /**
   * Validate all fields at once (for form submission)
   * @param {Object} formData - Complete form data
   * @returns {boolean} Whether all fields are valid
   */
  const validateAll = useCallback((formData) => {
    let isValid = true;
    const newErrors = {};
    const newTouched = {};

    // Validate main fields
    Object.keys(FIELD_VALIDATORS).forEach(fieldName => {
      const value = formData[fieldName];
      const result = FIELD_VALIDATORS[fieldName](value, formData);
      newTouched[fieldName] = true;
      
      if (!result.isValid) {
        newErrors[fieldName] = result.message;
        isValid = false;
      }
    });

    // Validate line items
    if (formData.items && Array.isArray(formData.items)) {
      formData.items.forEach((item, index) => {
        Object.keys(LINE_ITEM_VALIDATORS).forEach(field => {
          const value = item[field];
          const result = LINE_ITEM_VALIDATORS[field](value);
          const errorKey = `items[${index}].${field}`;
          newTouched[errorKey] = true;
          
          if (!result.isValid) {
            newErrors[errorKey] = result.message;
            isValid = false;
          }
        });
      });
    }

    setErrors(newErrors);
    setTouched(prev => ({ ...prev, ...newTouched }));

    return { isValid, errors: newErrors };
  }, []);

  // ============================================================================
  // BACKEND VALIDATION (Feature #7 - Backend is single source of truth)
  // ============================================================================
  
  // Track if backend validation is in progress
  const [isValidating, setIsValidating] = useState(false);
  
  // Track backend validation errors separately
  const [backendErrors, setBackendErrors] = useState({});
  
  // Debounce timer ref
  const validationTimeoutRef = useRef(null);
  
  /**
   * Normalize backend field paths to frontend format
   * Backend uses 'line_items[i].field', frontend uses 'items[i].field'
   */
  const normalizeFieldPath = useCallback((backendPath) => {
    return backendPath.replace(/^line_items\[/g, 'items[');
  }, []);
  
  /**
   * Validate form data against backend rules
   * @param {Object} formData - Complete form data
   * @param {string} invoiceType - Invoice type ID
   * @returns {Promise<boolean>} Whether validation passed
   */
  const validateWithBackend = useCallback(async (formData, invoiceType) => {
    if (!formData || !invoiceType) return true;
    
    // Build request data for backend
    const requestData = {
      ...formData,
      // Map items to line_items for backend
      line_items: (formData.items || []).map(item => ({
        description: item.description || '',
        quantity: parseFloat(item.quantity) || 0,
        rate: parseFloat(item.rate) || 0,
      })),
    };
    
    // Remove frontend-specific fields
    delete requestData.items;
    
    try {
      setIsValidating(true);
      
      const result = await validateInvoiceDraft({
        invoice_type: invoiceType,
        data: requestData,
        skip_invoice_number: true, // Backend generates invoice number on finalization
      });
      
      if (result.valid) {
        // Clear backend errors if validation passed
        setBackendErrors({});
        return true;
      }
      
      // Map backend errors to frontend format
      const newBackendErrors = {};
      (result.errors || []).forEach(error => {
        const normalizedField = normalizeFieldPath(error.field);
        newBackendErrors[normalizedField] = error.message;
      });
      
      setBackendErrors(newBackendErrors);
      return false;
    } catch (error) {
      // On API failure, don't block the user - local validation still applies
      console.error('Backend validation failed:', error);
      return true; // Assume valid if backend is unreachable
    } finally {
      setIsValidating(false);
    }
  }, [normalizeFieldPath]);
  
  /**
   * Debounced backend validation - call after form changes
   * @param {Object} formData - Complete form data
   * @param {string} invoiceType - Invoice type ID
   * @param {number} delay - Debounce delay in ms (default 500ms)
   */
  const validateWithBackendDebounced = useCallback((formData, invoiceType, delay = 500) => {
    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    
    // Set new timeout
    validationTimeoutRef.current = setTimeout(() => {
      validateWithBackend(formData, invoiceType);
    }, delay);
  }, [validateWithBackend]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);
  
  /**
   * Combined errors from local + backend validation
   * Backend errors take precedence for business rule enforcement
   */
  const allErrors = useMemo(() => ({
    ...errors,
    ...backendErrors, // Backend errors override local for same field
  }), [errors, backendErrors]);
  
  /**
   * Check if form has any validation errors (local or backend)
   */
  const hasAnyErrors = useMemo(() => 
    Object.keys(errors).length > 0 || Object.keys(backendErrors).length > 0, 
    [errors, backendErrors]
  );

  return {
    errors: allErrors, // Combined errors
    localErrors: errors, // Local validation only
    backendErrors, // Backend validation only
    touched,
    hasErrors: hasAnyErrors,
    isValidating, // Backend validation in progress
    validateField,
    validateLineItem,
    handleBlur,
    handleLineItemBlur,
    clearError,
    clearAllErrors,
    getError,
    getLineItemError,
    validateAll,
    // Backend validation functions
    validateWithBackend,
    validateWithBackendDebounced,
  };
}

export default useValidation;
